import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { confirmAlert } from "react-confirm-alert";
import { StyledDataTable, darkTheme } from "../components/TableStyles";
import { generateClientOptions } from "./Contractors";
import {
  getRoleAssessmentsLoading,
  getRoleAssessments,
  getSearchTerm
} from "../store/assessment/selectors";
import { getUserCompany } from "../store/users/selectors";
import {
  loadRoleAssessments,
  removeRoleAssessment,
  setSearchTerm
} from "../store/assessment/actions";
import { loadClients, setCurrentClient } from "../store/clients/actions";
import { getClients, getCurrentClient } from "../store/clients/selectors";
import { TitleArea, ContentArea, Empty, Wrapper } from "../components/Elements";
import noContractors from "../assets/images/no-contractors.svg";
import { getDisplayName } from "../components/ResultScreen";
import Button from "../components/Button";
import Loader from "../components/Loader";
import FormField from "../components/FormField";
import Combo from "../components/Combo";

const TableWrapper = styled.div`
  margin: 0px 20px;
  background-color: #fff;
  padding: 30px 20px;
  border-radius: 4px;
  flex: 1;
`;

const StyledCombo = styled(Combo)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const SearchWrapper = styled.div`
  width: 400px;
  margin-top: 20px;
`;

const Search = styled(FormField)`
  max-width: 300px;
  margin-top: 20px;
`;

function Contractors({ history }) {
  const dispatch = useDispatch();

  const loading = useSelector(getRoleAssessmentsLoading);

  const clients = useSelector(getClients);
  const currentClient = useSelector(getCurrentClient);
  const Assessments = useSelector(getRoleAssessments);
  const userCompany = useSelector(getUserCompany);
  const userCompanyId = userCompany && userCompany.id;
  const searchTerm = useSelector(getSearchTerm);

  const companyIdToUse =
    Number(currentClient) !== 0 ? currentClient : userCompanyId;

  useEffect(() => {
    if (currentClient) {
      dispatch(loadRoleAssessments(currentClient));
    }
  }, [currentClient, dispatch]);

  useEffect(() => {
    if (userCompany && !currentClient) {
      dispatch(setCurrentClient(userCompany.id));
    }
  }, [currentClient, dispatch, userCompany]);

  useEffect(() => {
    dispatch(loadClients());
  }, [dispatch]);

  const roleAssessments = useMemo(() => {
    const searchTermLower = searchTerm && searchTerm.toLowerCase();

    return Assessments.filter((roleAssessment) => {
      if (!searchTermLower) {
        return true;
      }

      const result =
        roleAssessment.assessment && roleAssessment.assessment.result;

      return (
        (roleAssessment.role_name &&
          roleAssessment.role_name.toLowerCase().includes(searchTermLower)) ||
        (result && result.toLowerCase().includes(searchTermLower))
      );
    });
  }, [Assessments, searchTerm]);

  const renderTable = () => {
    const deleteRow = (row) => {
      const options = {
        title: "Are you sure?",
        message: `Are you sure you want to delete this role assessment? It's not possible to reverse this operation`,
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              dispatch(removeRoleAssessment(row.id));
            },
          },
          {
            label: "No",
          },
        ],
      };
      confirmAlert(options);
    };

    const columns = [
      {
        name: "Role Name",
        selector: "role_name",
        sortable: true
      },
      {
        name: "Result",
        selector: (row) =>
          row.assessment
            ? getDisplayName(row.assessment.result, "FULL")
            : "Incomplete",
        sortable: true,
        cell: (row) =>
          row.assessment
            ? getDisplayName(row.assessment.result, "FULL")
            : "Incomplete",
      },
      {
        name : "Status",
        cell: (row) => 
          row.outOfDate ? "Requires Reassessment" : row.assessment ? "Up to date" : "N/A",

      },
      {
        cell: (row) => (
          <Button
            action
            type="danger"
            size="reduced"
            onClick={() => deleteRow(row)}
          >
            Delete
          </Button>
        ),
        button: true,
      },
    ];

    const handleRowClicked = (row) =>
      history.push(
        `/main/roles/assessment/${row.company_id}/rolebased/${row.id}`
      );

    return (
      <TableWrapper>
        <StyledDataTable
          noHeader
          pagination
          customTheme={darkTheme}
          columns={columns}
          data={roleAssessments}
          fixedHeader
          highlightOnHover
          fixedHeaderScrollHeight="100%"
          title="All Roles"
          onRowClicked={handleRowClicked}
        />
      </TableWrapper>
    );
  };

  let clientOptions = [];
  if (clients) {
    clientOptions = generateClientOptions(clients, userCompany);
  }

  return (
    <Wrapper>
      <TitleArea>
        <div className="title">
          <h1>Roles</h1>

          {clients && clients.length ? (
            <StyledCombo
              options={clientOptions}
              value={currentClient}
              onChange={(newValue) => {
                dispatch(setCurrentClient(newValue));
              }}
            />
          ) : null}
          <SearchWrapper>
            <Search
              placeholder="Search"
              type="text"
              changeMonitor={(value) => dispatch(setSearchTerm(value))}
              className="search"
              autoFocus={true}
              value={searchTerm}
            />
          </SearchWrapper>
        </div>
        <div className="controls">
          <Button
            action
            onClick={() => {
              history.push(
                `/main/roles/assessment/${companyIdToUse}/rolebased/new`
              );
            }}
          >
            New Role
          </Button>
        </div>
      </TitleArea>
      <ContentArea>
        {loading && !roleAssessments.length ? (
          <Loader />
        ) : roleAssessments && roleAssessments.length ? (
          renderTable()
        ) : (
          <div>
            <Empty>
              <img src={noContractors} alt="" />
              <h2>You've not created any roles yet</h2>
              <p>You can add one by click on 'New Role' above</p>
            </Empty>
          </div>
        )}
      </ContentArea>
    </Wrapper>
  );
}

export default Contractors;
